import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService, PriceChangeItem, PriceChangesResponse } from '../services/api';

// Helper to format date for URL (YYYY-MM-DD)
const formatDateForUrl = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

// Helper to format date for display (DD/MM/YYYY)
const formatDateForDisplay = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

// Helper to format timestamp to Europe/Lisbon
const formatTimestamp = (timestamp: string | null): string => {
  if (!timestamp) return '-';
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('pt-PT', {
      timeZone: 'Europe/Lisbon',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch {
    return timestamp;
  }
};

const PriceChangesPage: React.FC = () => {
  // URL params for persistence
  const [searchParams, setSearchParams] = useSearchParams();

  // Get date from URL or default to today
  const dateFromUrl = searchParams.get('date');
  const today = formatDateForUrl(new Date());
  const initialDate = dateFromUrl || today;

  // State
  const [selectedDate, setSelectedDateState] = useState<string>(initialDate);
  const [priceChanges, setPriceChanges] = useState<PriceChangeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<{
    total: number;
    increases: number;
    decreases: number;
    snapshotTimestamp: string | null;
  }>({ total: 0, increases: 0, decreases: 0, snapshotTimestamp: null });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 100;

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState<'mudanca_pct_desc' | 'mudanca_pct_asc' | 'position_asc' | 'position_desc' | 'price_change_desc' | 'price_change_asc'>('mudanca_pct_desc');

  // Wrapper to sync date with URL
  const setSelectedDate = useCallback((date: string) => {
    setSelectedDateState(date);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('date', date);
    setSearchParams(newParams, { replace: true });
    setCurrentPage(1);
  }, [searchParams, setSearchParams]);

  // Load price changes
  const loadPriceChanges = useCallback(async (date: string) => {
    try {
      setLoading(true);
      setError(null);

      const offset = (currentPage - 1) * pageSize;

      const response: PriceChangesResponse = await apiService.getPriceChanges(date, {
        limit: pageSize,
        offset: offset,
        order_by: orderBy,
        search: searchTerm || undefined
      });

      setPriceChanges(response.results);
      setTotalCount(response.total_count);
      setStats({
        total: response.total_count,
        increases: response.increases_count,
        decreases: response.decreases_count,
        snapshotTimestamp: response.snapshot_timestamp
      });
    } catch (err: any) {
      console.error('Error loading price changes:', err);
      if (err.response?.status === 404) {
        setError('Nenhum snapshot de ranking encontrado para esta data.');
        setPriceChanges([]);
        setStats({ total: 0, increases: 0, decreases: 0, snapshotTimestamp: null });
      } else {
        setError('Erro ao carregar dados. Por favor, tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, orderBy, searchTerm]);

  // Load data when date or filters change
  useEffect(() => {
    if (selectedDate) {
      loadPriceChanges(selectedDate);
    }
  }, [selectedDate, loadPriceChanges]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, orderBy]);

  // Handle SKU click
  const handleSkuClick = (sku: string) => {
    window.open(`/products/${sku}`, '_blank');
  };

  // Handle date change
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
  };

  // Navigate date
  const navigateDate = (days: number) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + days);
    setSelectedDate(formatDateForUrl(current));
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / pageSize);

  // Format price change percentage with color
  const formatPriceChange = (pct: number | null): JSX.Element => {
    if (pct === null || pct === undefined) return <span>-</span>;

    const sign = pct > 0 ? '+' : '';
    const color = pct > 0 ? 'text-danger' : 'text-success';

    return (
      <span className={`fw-bold ${color}`}>
        {sign}{pct.toFixed(2)}%
      </span>
    );
  };

  // Format impact with color
  const formatImpact = (pct: number | null): JSX.Element => {
    if (pct === null || pct === undefined) return <span>-</span>;

    const sign = pct > 0 ? '+' : '';
    const color = pct > 0 ? 'text-success' : 'text-danger';

    return (
      <span className={color}>
        {sign}{pct.toFixed(1)}%
      </span>
    );
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1>Mudancas de Preco</h1>
          </div>
        </div>
      </div>

      {/* Date selector */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <label className="form-label fw-bold">Data</label>
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigateDate(-1)}
                  title="Dia anterior"
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                <input
                  type="date"
                  className="form-control"
                  value={selectedDate}
                  onChange={handleDateChange}
                  max={today}
                />
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigateDate(1)}
                  disabled={selectedDate >= today}
                  title="Dia seguinte"
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={() => setSelectedDate(today)}
                  disabled={selectedDate === today}
                >
                  Hoje
                </button>
              </div>
              {stats.snapshotTimestamp && (
                <small className="text-muted mt-2 d-block">
                  Snapshot: {formatTimestamp(stats.snapshotTimestamp)}
                </small>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats cards */}
      {!loading && !error && stats.total > 0 && (
        <div className="row mb-4">
          <div className="col-md-4">
            <div className="card bg-primary text-white">
              <div className="card-body text-center">
                <h5 className="card-title">Total Mudancas</h5>
                <h2>{stats.total}</h2>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card bg-danger text-white">
              <div className="card-body text-center">
                <h5 className="card-title">Aumentos</h5>
                <h2>{stats.increases}</h2>
                <small>({stats.total > 0 ? ((stats.increases / stats.total) * 100).toFixed(1) : 0}%)</small>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card bg-success text-white">
              <div className="card-body text-center">
                <h5 className="card-title">Reducoes</h5>
                <h2>{stats.decreases}</h2>
                <small>({stats.total > 0 ? ((stats.decreases / stats.total) * 100).toFixed(1) : 0}%)</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="row mb-3">
        <div className="col-md-4">
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Pesquisar por SKU..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-4">
          <select
            className="form-select"
            value={orderBy}
            onChange={(e) => setOrderBy(e.target.value as typeof orderBy)}
          >
            <option value="mudanca_pct_desc">Maior Mudanca % (abs)</option>
            <option value="mudanca_pct_asc">Menor Mudanca % (abs)</option>
            <option value="price_change_desc">Maiores Aumentos</option>
            <option value="price_change_asc">Maiores Reducoes</option>
            <option value="position_asc">Melhor Posicao</option>
            <option value="position_desc">Pior Posicao</option>
          </select>
        </div>
        <div className="col-md-4 text-end">
          <span className="text-muted">
            {totalCount} produtos com mudanca de preco
          </span>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert-warning" role="alert">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">A carregar...</span>
          </div>
          <p className="mt-2 text-muted">A carregar dados...</p>
        </div>
      )}

      {/* Table */}
      {!loading && !error && priceChanges.length > 0 && (
        <>
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead className="table-dark">
                <tr>
                  <th style={{ width: '60px' }}>Pos</th>
                  <th style={{ width: '120px' }}>SKU</th>
                  <th>Produto</th>
                  <th style={{ width: '100px' }} className="text-end">Preco Ant.</th>
                  <th style={{ width: '100px' }} className="text-end">Preco Atual</th>
                  <th style={{ width: '100px' }} className="text-end">Mudanca %</th>
                  <th style={{ width: '100px' }} className="text-end">Impacto DPD</th>
                  <th style={{ width: '80px' }} className="text-end">Score</th>
                  <th style={{ width: '80px' }} className="text-end">DPD 7</th>
                  <th style={{ width: '80px' }} className="text-end">Stock</th>
                  <th>Categoria</th>
                </tr>
              </thead>
              <tbody>
                {priceChanges.map((item, index) => (
                  <tr key={item.sku}>
                    <td className="text-center">{item.ranking_position || '-'}</td>
                    <td>
                      <button
                        className="btn btn-link btn-sm p-0 text-primary"
                        onClick={() => handleSkuClick(item.sku)}
                        title="Ver detalhes do produto"
                      >
                        {item.sku}
                      </button>
                    </td>
                    <td>
                      <span className="text-truncate d-inline-block" style={{ maxWidth: '250px' }} title={item.product_name || ''}>
                        {item.product_name || '-'}
                      </span>
                    </td>
                    <td className="text-end">
                      {item.preco_anterior ? `${item.preco_anterior.toFixed(2)}` : '-'}
                    </td>
                    <td className="text-end">
                      {item.preco_atual ? `${item.preco_atual.toFixed(2)}` : '-'}
                    </td>
                    <td className="text-end">
                      {formatPriceChange(item.mudanca_preco_pct)}
                    </td>
                    <td className="text-end">
                      {formatImpact(item.impacto_previsto_dpd_pct)}
                    </td>
                    <td className="text-end">
                      {item.final_score ? item.final_score.toFixed(4) : '-'}
                    </td>
                    <td className="text-end">
                      {item.dpd_7 ? item.dpd_7.toFixed(2) : '-'}
                    </td>
                    <td className="text-end">
                      {item.stock_quantity}
                    </td>
                    <td>
                      <span className="text-truncate d-inline-block" style={{ maxWidth: '150px' }} title={item.categoria_nome || ''}>
                        {item.categoria_nome || '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="mt-3">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    Primeira
                  </button>
                </li>
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(p => p - 1)}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </button>
                </li>
                <li className="page-item active">
                  <span className="page-link">
                    Pagina {currentPage} de {totalPages}
                  </span>
                </li>
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(p => p + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Proxima
                  </button>
                </li>
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Ultima
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
      )}

      {/* Empty state */}
      {!loading && !error && priceChanges.length === 0 && stats.snapshotTimestamp && (
        <div className="alert alert-info text-center" role="alert">
          Nenhuma mudanca de preco encontrada para a data {formatDateForDisplay(selectedDate)}.
        </div>
      )}
    </div>
  );
};

export default PriceChangesPage;
